import { IClinic, IProduct } from "@interfaces";
import React, { memo, useEffect, useState } from "react";
import * as Yup from "yup";
import {
  Input,
  Form,
  Row,
  Col,
  FormInstance,
  Divider,
  Upload,
  Button,
  UploadProps,
  DatePicker,
} from "antd";
import { useYup } from "@validations";
import { useTranslation } from "react-i18next";
import { PATH_CLINIC } from "@configs";
import { useDispatch } from "react-redux";
import { setLoading } from "@redux";

import { useNotify } from "@utils";
import { useHistory } from "react-router";
import { SharedButton } from "@components";
import styled from "styled-components";
import { UploadOutlined } from "@ant-design/icons";
import { productApi } from "@api";
const { TextArea } = Input;

interface IProps {
  product?: IProduct;
}

const initValue: Partial<IProduct> = {
  productName: "",
  mainGroup: "",
  subGroup: "",
  sku: "",
  strengthValue: "",
  category: "",
  subCategory: "",
  packageSize: 0,
  authorizedDistributer: "",
  minPruchaseQty: 0,
  purchaseIncreaseCount: 0,
  sellingUnit: "",
  sellingPrice: 0,
  manufacturerName: "",
  madeInCountry: "",
  chemicalName: "",
  actionAndIndication: "",
  dosage: "",
  potentialSideEffect: "",
  productDescription: "",
  productImage: "",
};

export const ProductForm = memo(({ product }: IProps) => {
  const formRef = React.useRef<FormInstance>(null);
  const { YupProduct } = useYup();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { success, error } = useNotify();
  const productSchema = Yup.object().shape(YupProduct);
  const [data, setData] = useState<IProduct>(initValue);
  const [clinicForm] = Form.useForm();

  useEffect(() => {
    if (product) {
      setData({ ...data, ...product });
      formRef.current?.setFieldsValue({
        ...data,
        ...product,
      });
    }
  }, [product]);

  const onFinish = async (values: IProduct) => {
    try {
      dispatch(setLoading(true));
      const res = await productApi.create(values);
      await console.log(res);
      success(t("message.create.success"));
      history.push(PATH_CLINIC);
    } catch (err: any) {
      error(err?.response?.message);
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  const yupSync = {
    async validator({ field }: any, value: any) {
      await productSchema.validateSyncAt(field, { [field]: value });
    },
  };

  const goBack = () => history.goBack();

  const propsUpload: UploadProps = {
    name: "file",
    action: "https://run.mocky.io/v3/435e224c-44fb-4773-9faf-380c5e6a2188",
    headers: {
      authorization: "authorization-text",
    },
    onChange(info) {
      if (info.file.status !== "uploading") {
        console.log(info.file, info.fileList);
      }
      if (info.file.status === "done") {
        success(`${info.file.name} file uploaded successfully`);
      } else if (info.file.status === "error") {
        error(`${info.file.name} file upload failed.`);
      }
    },
  };
  return (
    <>
      <Form
        form={clinicForm}
        name="basic"
        ref={formRef}
        initialValues={data}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
      >
        <Row gutter={{ md: 54, lg: 54 }}>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.product_name")} name="productName" rules={[yupSync]}>
              <Input type="text" />
            </Form.Item>
          </Col>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.main_group")} name="mainGroup" rules={[yupSync]}>
              <Input type="text" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={{ md: 54, lg: 54 }}>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.sub_group")} name="subGroup" rules={[yupSync]}>
              <Input type="text" />
            </Form.Item>
          </Col>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.sku_code")} name="sku" rules={[yupSync]}>
              <Input type="text" />
            </Form.Item>
          </Col>
        </Row>
        <Divider />
        <Row gutter={{ md: 54, lg: 54 }}>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.strength_value")} name="strengthValue" rules={[yupSync]}>
              <Input type="number" />
            </Form.Item>
          </Col>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.category")} name="category" rules={[yupSync]}>
              <Input type="text" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={{ md: 54, lg: 54 }}>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.sub_category")} name="subCategory" rules={[yupSync]}>
              <Input type="text" />
            </Form.Item>
          </Col>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.expiry_date")} name="expiryDate" rules={[yupSync]}>
              <DatePicker />
            </Form.Item>
          </Col>
        </Row>
        <Divider />
        <Row gutter={{ md: 54, lg: 54 }}>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.pack_size")} name="packageSize" rules={[yupSync]}>
              <Input type="number" />
            </Form.Item>
          </Col>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item
              label={t("page.authorized_distributer")}
              name="authorizedDistributer"
              rules={[yupSync]}
            >
              <Input type="text" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={{ md: 54, lg: 54 }}>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item
              label={t("page.min_purchase_amount")}
              name="minPruchaseQty"
              rules={[yupSync]}
            >
              <Input type="number" />
            </Form.Item>
          </Col>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item
              label={t("page.purchase_increase_count")}
              name="purchaseIncreaseCount"
              rules={[yupSync]}
            >
              <Input type="number" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={{ md: 54, lg: 54 }}>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.selling_unit")} name="sellingUnit" rules={[yupSync]}>
              <Input type="text" />
            </Form.Item>
          </Col>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.selling_price")} name="sellingPrice" rules={[yupSync]}>
              <Input type="text" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={{ md: 54, lg: 54 }}>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.manufacture_name")} name="manufacturerName" rules={[yupSync]}>
              <Input type="text" />
            </Form.Item>
          </Col>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.made_in_country")} name="madeInCountry" rules={[yupSync]}>
              <Input type="text" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={{ md: 54, lg: 54 }}>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.chemical_name")} name="chemicalName" rules={[yupSync]}>
              <Input type="text" />
            </Form.Item>
          </Col>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.dosage")} name="dosage" rules={[yupSync]}>
              <Input type="text" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={{ md: 54, lg: 54 }}>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item
              label={t("page.action_and_indication")}
              name="actionAndIndication"
              rules={[yupSync]}
            >
              <TextArea showCount maxLength={100} placeholder="Enter action and indication" />
            </Form.Item>
          </Col>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item
              label={t("page.potential_side_effects")}
              name="potentialSideEffect"
              rules={[yupSync]}
            >
              <TextArea showCount maxLength={100} placeholder="Enter Potential side effects" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={{ md: 54, lg: 54 }}>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item
              label={t("page.product_description")}
              name="productDescription"
              rules={[yupSync]}
            >
              <TextArea showCount maxLength={100} placeholder="Enter Product Description" />
            </Form.Item>
          </Col>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.upload_image")} name="productImage">
              <Upload {...propsUpload}>
                <Button icon={<UploadOutlined />}>Click to Upload</Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>
        <Form.Item>
          <StyledFormAction>
            <SharedButton type="default" htmlType="button" size="middle" onClick={goBack}>
              {t("page.cancel")}
            </SharedButton>
            <SharedButton type="primary" htmlType="submit" size="middle">
              {t("page.create")}
            </SharedButton>
          </StyledFormAction>
        </Form.Item>
      </Form>
    </>
  );
});

const StyledFormAction = styled.div`
  display: flex;
  justify-content: flex-end;
  column-gap: 40px;
`;
